/**
 * One section of a structured document in its editor: a head (title, state badge, audience switch, move up and down),
 * the Markdown text and the caller's actions. Three states:
 *
 * - `suggested`: a template section with no text yet; folded to its head until **Write** opens it;
 * - `filled`: a saved section;
 * - `custom`: a section the author added, whose title is a field.
 *
 * The move buttons are how the order changes from the keyboard (no drag and drop). The caller holds the draft and
 * does the saving; warnings from the last save are shown under the text, announced politely.
 */

import { useId, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Lock, PenLine } from 'lucide-react';

import { sectionInTitleOneMax } from '@/api/generated/schemas';

import { Badge } from './Badge';
import { FieldError, fieldA11y } from './FieldError';
import { MarkdownEditor } from './MarkdownEditor';

export type SectionCardState = 'suggested' | 'filled' | 'custom';
export type SectionAudienceValue = 'internal' | 'shareable';

const STATE_WORDS: Record<SectionCardState, string> = { suggested: 'Suggested', filled: 'Filled', custom: 'Custom' };

export interface SectionCardProps {
  title: string;
  state: SectionCardState;
  /** A custom section's title field; without it the title is fixed. */
  onTitle?: (title: string) => void;
  audience: SectionAudienceValue;
  onAudience: (audience: SectionAudienceValue) => void;
  /** Why the audience cannot change (it stays visible, disabled, with this reason). */
  audienceLocked?: string;
  body: string;
  onBody: (body: string) => void;
  maxLength: number;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  /** Lines from the last save, such as a client named in shareable text. */
  warnings?: string[];
  /** Save, Remove: the caller's buttons, after the text. */
  actions?: ReactNode;
  /** The server's 422 messages for the title and the text, shown beside each. */
  errors?: { title?: string; body?: string };
}

function AudienceSwitch({ title, audience, onAudience, locked }: { title: string; audience: SectionAudienceValue; onAudience: (a: SectionAudienceValue) => void; locked?: string }) {
  const options: { key: SectionAudienceValue; label: string }[] = [
    { key: 'shareable', label: 'Shareable' },
    { key: 'internal', label: 'Internal' },
  ];
  return (
    <div className="segmented section-card-audience" role="radiogroup" aria-label={`Audience of ${title}`} title={locked}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={audience === o.key}
          disabled={Boolean(locked)}
          onClick={() => onAudience(o.key)}
        >
          {o.key === 'internal' && <Lock aria-hidden="true" />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MoveButtons({ title, onMoveUp, onMoveDown }: { title: string; onMoveUp?: () => void; onMoveDown?: () => void }) {
  if (!onMoveUp && !onMoveDown) return null;
  return (
    <span className="section-card-move">
      <button type="button" className="icon-btn" aria-label={`Move ${title} up`} title="Move up" disabled={!onMoveUp} onClick={onMoveUp}>
        <ArrowUp aria-hidden="true" />
      </button>
      <button type="button" className="icon-btn" aria-label={`Move ${title} down`} title="Move down" disabled={!onMoveDown} onClick={onMoveDown}>
        <ArrowDown aria-hidden="true" />
      </button>
    </span>
  );
}

export function SectionCard(props: SectionCardProps) {
  const { title, state, onTitle, body, onBody, maxLength, warnings = [], actions, errors = {} } = props;
  const id = useId();
  const [open, setOpen] = useState(state !== 'suggested' || body !== '');
  const shown = title.trim() || 'Untitled section';

  return (
    <section className={`card section-card section-card-${state}`} aria-labelledby={`${id}-title`}>
      <div className="section-card-head">
        <h3 id={`${id}-title`} className="section-card-title">{shown}</h3>
        <Badge tone={state === 'suggested' ? 'neutral' : 'info'}>{STATE_WORDS[state]}</Badge>
        <span className="section-card-tools">
          <AudienceSwitch title={shown} audience={props.audience} onAudience={props.onAudience} locked={props.audienceLocked} />
          <MoveButtons title={shown} onMoveUp={props.onMoveUp} onMoveDown={props.onMoveDown} />
        </span>
      </div>
      {open ? (
        <div className="section-card-body">
          {onTitle && (
            <div className="field">
              <label htmlFor={`${id}-name`}>Section title</label>
              <input id={`${id}-name`} type="text" maxLength={sectionInTitleOneMax} value={title} {...fieldA11y(`${id}-name`, errors.title)} onChange={(e) => onTitle(e.target.value)} />
              <FieldError inputId={`${id}-name`} message={errors.title} />
            </div>
          )}
          <MarkdownEditor
            id={`${id}-text`}
            value={body}
            onChange={onBody}
            maxLength={maxLength}
            rows={6}
            required={false}
            headingPrefix="### "
            error={errors.body}
            placeholder={`Write ${shown}.`}
          />
          <div aria-live="polite">
            {warnings.map((w) => (
              <p key={w} className="alert alert-warning section-card-warning">{w}</p>
            ))}
          </div>
          {actions && <div className="section-card-actions">{actions}</div>}
        </div>
      ) : (
        <div className="section-card-body section-card-folded">
          <p className="hint">Suggested for this kind of asset. Readers do not see it until it has text.</p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>
            <PenLine aria-hidden="true" /> Write {shown}
          </button>
        </div>
      )}
    </section>
  );
}
